/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import {MutableRefObject, useCallback, useEffect, useMemo, useRef} from 'react';
import {useSignals} from '@preact/signals-react/runtime';
import {translate} from '@ion/core/src/locale/locale';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {getDashboadHierarchy} from '@ion/core/src/services/Reports';
import Sisense from '@ion/core/src/services/Sisense';
import {RootFolder, SisenseState} from '@ion/core/src/services/SisenseUtils';
import {GenericObject} from '@ion/core/src/types/entities';
import {Preference} from '@ion/core/src/types/preferences';
import {smoothScrollByElement} from '@ion/core/src/utils/BrowserUtils';
import {safeJSONStringify} from '@ion/core/src/utils/JSONUtils';
import {useThemeMode} from '@ion/dls/src/theme/useThemeContext';
import QueryKey from '@ion/query/src/config/QueryKeys';
import useSearchById from '@ion/query/src/hooks/useSearchById';
import {removeQueries} from '@ion/query/src/utils/helpers';
import useDashboardIFramePath from '../../sisense/hooks/useDashboardIFramePath';
import useSisenseSelector from '../../sisense/hooks/useSisenseSelector';
import {
  autoSelectCategory,
  categoryFilter,
  headerTab,
  lastDashboardHeaderTab,
  mobileOpen,
  openCategory,
  scheduledReportsContext,
  selectedCategory,
  selectedDashboard,
  setAutoSelectCategory,
  setCategoryFilter as _setCategoryFilter,
  setMobileOpen,
  setOpenCategory,
  setSelectedCategory,
  setSelectedDashboard,
  setSideTab,
  sideTab,
  updateHeaderTab
} from './store';
import {Headertabs, SideNavTabs} from './types';
import {findDashboardPath, formatDashboards, transformTree} from './utils';

/**
 * Return type of the useDashboardManager hook.
 */
export interface DashboardManager {
  mobileOpen: boolean;
  setMobileOpen: (open: boolean) => void;
  openCategory: Record<string, boolean>;
  selectedCategory: string | null;
  selectedDashboard: any;
  setSelectedDashboard: (dashboard: any) => void;
  sideTab: SideNavTabs;
  setSideTab: (tab: SideNavTabs) => void;
  headerTab: Headertabs;
  lastDashboardHeaderTab: Headertabs;
  scheduledReportsContext: {
    entryMode: 'direct' | 'dashboardRow';
    originHeaderTab: Headertabs | null;
  };
  setHeaderTab: (event: any, newValue: Headertabs) => void;
  refetch: () => void;
  onRefreshAfterError: () => void;
  isLoading: boolean;
  isError: any;
  path: string;
  formattedCategories: any[];
  allDashboardCategories: any[];
  allDashOIDs: string[];
  toggleCategory: (categoryName: string | string[], value?: boolean, tab?: SideNavTabs) => void;
  ref: MutableRefObject<any>;
  onSavedReportLoad: (dashboard: any) => void;
  setCategoryFilter: (categories: string[], headerTab?: Headertabs, sideTab?: SideNavTabs) => void;
  categoryFilter: string[];
};

/**
 * Hook to manage the dashboard state including selected dashboard, 
 * category, loading/error states, and navigation tabs.
 *
 * @returns - Dashboard management helpers and state.
 */
const useDashboardManager = (features?: GenericObject[]): DashboardManager => {
  useSignals();
  const sisenseOptions      = useSisenseSelector(SisenseState.ServerOptions);
  const isSisenseLoading    = useSisenseSelector(SisenseState.SisenseLoading, !sisenseOptions);
  const sisenseOptionsError = useSisenseSelector(SisenseState.SisenseOptionsError);
  const sessionCreated      = useSisenseSelector(SisenseState.SessionCreated);
  const ref                 = useRef<any>({});
  // the folder colors re-resolve on a light/dark switch ...
  const {mode}              = useThemeMode();

  const {loading, result: response, refetch, error} = useSearchById({
    api       : getDashboadHierarchy,
    id        : 'sisense-dashboards',
    baseKey   : QueryKey.SISENSE_DASHBOARD_SEARCH,
    enabled   : !!sessionCreated && !sisenseOptionsError && !!sisenseOptions,
    staleTime : 15 * 60 * 1000
  });

  const result = useMemo(() => {
    if (response) {
      return transformTree(response);
    }
    return response;
  }, [response]);

  useEffect(() => {
    Sisense.startSessionRefresh();
    return () => {
      Sisense.stopSessionRefresh();
    };
  }, []);

  const isLoading = isSisenseLoading || loading;
  const isError   = sisenseOptionsError || error;
  const isScheduledReportsView = headerTab.value === Headertabs.ScheduledReports;
  const borrowedHeaderTab = scheduledReportsContext.value.originHeaderTab ?? lastDashboardHeaderTab.value;
  const effectiveHeaderTab = headerTab.value === Headertabs.ScheduledReports
    ? borrowedHeaderTab
    : headerTab.value;
  const effectiveOpenCategory = openCategory.value?.[effectiveHeaderTab]?.[sideTab.value] || {};
  const effectiveSelectedCategory = selectedCategory.value?.[effectiveHeaderTab]?.[sideTab.value] || null;
  const effectiveCategoryFilter = categoryFilter.value?.[effectiveHeaderTab]?.[sideTab.value] || null;

  useEffect(() => {
    if (!isLoading && result) {
      const elementID = selectedDashboard.value?.title ?? selectedCategory.value;
      if (elementID) {
        const timeout = setTimeout(() => {
          clearTimeout(timeout);
          smoothScrollByElement(elementID);
        }, 600);
      }
    }
  }, [isLoading, result]);

  useEffect(() => {
    if (!selectedDashboard.value &&
      (effectiveHeaderTab === Headertabs.Recent ||
        sideTab.value === SideNavTabs.MyReports)
    ) {
      const t = setTimeout(() => {
        refetch();
        clearTimeout(t);
      }, 350);
    }
  }, [
    selectedDashboard.value,
    sideTab.value,
    effectiveHeaderTab
  ]);

  useEffect(() => {
    if (sisenseOptions && !isLoading && !!(result as any).data) {
      removeQueries(QueryKey.SISENSE_DASHBOARD_SEARCH, false);
      refetch();
    }
  }, [sisenseOptions]);

  useEffect(() => {
    if (!isLoading) {
      const prevState = UserPreferences.get(Preference.analyticsView) || {};
      const state = {
        headerTab              : headerTab.value,
        lastDashboardHeaderTab : lastDashboardHeaderTab.value,
        sideTab                : sideTab.value,
        selectedCategory       : isScheduledReportsView
          ? (prevState.selectedCategory || {})
          : selectedCategory.value,
        openCategory : isScheduledReportsView
          ? (prevState.openCategory || {})
          : openCategory.value,
        categoryFilter : isScheduledReportsView
          ? (prevState.categoryFilter || {})
          : categoryFilter.value
      };
      if (safeJSONStringify(state) !== safeJSONStringify(prevState)) {
        UserPreferences.set(Preference.analyticsView, state, true);
      }
    }
  }, [
    sideTab.value,
    headerTab.value,
    lastDashboardHeaderTab.value,
    selectedCategory.value,
    openCategory.value,
    categoryFilter.value,
    scheduledReportsContext.value,
    isLoading
  ]);

  const path = useDashboardIFramePath(false, {
    showLeftPanel : false,
    features      : ['sidenav'],
    oid           : selectedDashboard.value?.oid
  });

  const {dashboardCategories: formattedCategories, allDashboardCategories, allDashOIDs} = useMemo(() => {
    if (!loading && result) {
      const current = formatDashboards({
        search                  : (result as any).data,
        tab                     : sideTab.value,
        filterByDate            : effectiveHeaderTab === Headertabs.Recent,
        returnOnlyUncategorized : sideTab.value === SideNavTabs.MyReports,
        generateColor           : true,
        features
      });

      const allDashboardCategories = [...current.dashboardCategories];
      const restTabs = [SideNavTabs.Basic, SideNavTabs.Advanced, SideNavTabs.MyReports];

      for (const tab of restTabs) {
        if (tab === sideTab.value) continue;
        const others = formatDashboards({
          search                  : (result as any).data,
          filterByDate            : effectiveHeaderTab === Headertabs.Recent,
          returnOnlyUncategorized : tab === SideNavTabs.MyReports,
          generateColor           : true,
          features,
          tab
        });
        allDashboardCategories.push(...others.dashboardCategories);
      }

      return {...current, allDashboardCategories};
    }
    return {dashboardCategories: [], allDashboardCategories: [], allDashOIDs: []};
  }, [loading, (result as any).data, sideTab.value, effectiveHeaderTab, mode]);

  useEffect(() => {
    ref.current = {
      headerTab        : effectiveHeaderTab,
      sideTab          : sideTab.value,
      selectedCategory : selectedCategory.value,
      openCategory     : openCategory.value,
      categoryFilter   : categoryFilter.value,
      allDashOIDs,
      setSideTab
    };
  }, [
    selectedCategory.value,
    categoryFilter.value,
    openCategory.value,
    effectiveCategoryFilter,
    effectiveHeaderTab,
    effectiveOpenCategory,
    effectiveSelectedCategory,
    scheduledReportsContext.value,
    sideTab.value,
    allDashOIDs,
    setSideTab
  ]);
  
  const toggleCategory = useCallback((categoryName: string, value?: boolean, tab = ref.current.sideTab) => {
    const findParentPath = (
      categories: any[],
      targetName: string,
      path: string[] = []
    ): string[] | null => {
      for (const {name, folders} of categories) {
        const newPath = [...path, name];
        if (name === targetName) {
          return newPath;
        }
        const childPath = findParentPath(folders ?? [], targetName, newPath);
        if (childPath) {
          return childPath;
        }
      }
    };
    const prevState     = ref.current.openCategory?.[ref.current.headerTab]?.[tab] ?? {};
    let newOpenCategory = {...prevState};

    if (Array.isArray(categoryName)) {
      const namesToToggle = categoryName;
      const lastName = namesToToggle.at(-1) || '';
      namesToToggle.forEach(name => {
        const {[name]: _, ...rest} = newOpenCategory;
        newOpenCategory = {...rest};
      });

      namesToToggle.forEach(name => {
        const nextValue = value ?? !prevState[name];
        newOpenCategory[name] = nextValue;

        if (nextValue) {
          const parentPath = findParentPath(formattedCategories, name) ?? [];
          parentPath.forEach(parent => { newOpenCategory[parent] = true; });
        }
      });
      const lastValue = value ?? !prevState[lastName];
      delete newOpenCategory[lastName];
      newOpenCategory[lastName] = lastValue;
    } else {
      const {[categoryName]: _, ...rest} = prevState;
      newOpenCategory = {
        ...rest,
        [categoryName] : value ?? !prevState[categoryName]
      };
      if (newOpenCategory[categoryName]) {
        const parentPath = findParentPath(formattedCategories, categoryName) ?? [];
        parentPath.forEach(name => { newOpenCategory[name] = true; });
      }
    }
    const newSelectedCategory = Object.entries(newOpenCategory)
      .filter(([_, isOpen]) => isOpen)
      .map(([key]) => key)
      .at(-1);

    setOpenCategory?.({
      ...ref.current.openCategory,
      [ref.current.headerTab] : {
        ...ref.current.openCategory[ref.current.headerTab],
        [tab] : newOpenCategory
      }
    });
    setSelectedCategory?.({
      ...ref.current.selectedCategory,
      [ref.current.headerTab] : {
        ...ref.current.selectedCategory?.[ref.current.headerTab],
        [tab] : newSelectedCategory
      }
    });
  }, [
    formattedCategories,
    openCategory
  ]);

  const setCategoryFilter = useCallback((categories: string[], headerTb?: Headertabs, sideTb?: SideNavTabs) => {
    _setCategoryFilter?.({
      ...ref.current.categoryFilter,
      [headerTb ?? ref.current.headerTab] : {
        ...ref.current.categoryFilter?.[headerTb ?? ref.current.headerTab],
        [sideTb ?? ref.current.sideTab] : categories
      }
    });
  }, []);

  const setHeaderTab = useCallback(function(_event, newValue) {
    updateHeaderTab(newValue);
  }, []);

  const onSavedReportLoad = useCallback((dashboard) => {
    (async function() {
      (ref.current.sideTab !== SideNavTabs.MyReports) && ref.current.setSideTab(SideNavTabs.MyReports);
      if (!ref.current.allDashOIDs.includes(dashboard?.oid)) {
        await refetch();
        toggleCategory(translate('sisense:analytics.folderNames.myReports'), true);
        setSelectedDashboard(dashboard);
      }
    })();
  }, []);

  const onRefreshAfterError = useCallback(() => {
    (async function() {
      Sisense.retrieveOptions();
      refetch();
    })();
  }, []);

  useEffect(() => {
    const results        = (result as any)?.data;
    const selectedDashId = selectedDashboard.value?.oid;
    if (!loading && results && autoSelectCategory.value && selectedDashId) {
      const [sideTab, ...rest] = findDashboardPath(results, selectedDashId);
      if (sideTab) {
        setSideTab(sideTab === RootFolder.Basic ? SideNavTabs.Basic : SideNavTabs.Advanced);
        if (rest?.length) {
          const timeout = setTimeout(() => {
            clearTimeout(timeout);
            toggleCategory(rest[rest.length - 1], true);
          }, 300);
        }
      }
      setAutoSelectCategory(false);
    }
  }, [loading, result, selectedDashboard.value, autoSelectCategory.value]);

  return {
    mobileOpen              : mobileOpen.value,
    headerTab               : headerTab.value,
    lastDashboardHeaderTab  : lastDashboardHeaderTab.value,
    scheduledReportsContext : scheduledReportsContext.value,
    sideTab                 : sideTab.value,
    openCategory            : effectiveOpenCategory,
    selectedCategory        : effectiveSelectedCategory,
    categoryFilter          : effectiveCategoryFilter,
    selectedDashboard       : selectedDashboard.value,
    allDashOIDs,
    setMobileOpen,
    setSelectedDashboard,
    setCategoryFilter,
    onSavedReportLoad,
    setSideTab,
    setHeaderTab,
    toggleCategory,
    refetch,
    onRefreshAfterError,
    isLoading,
    isError,
    path,
    ref,
    allDashboardCategories,
    formattedCategories
  };
};

export default useDashboardManager;
